import {Component, EventEmitter, HostListener, Input, Output, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';

export enum SortDirection {
  None = 0,
  Ascending = 1,
  Descending = 2
}

export enum DataType {
  String = 0,
  Date = 1,
  Number = 2,
  Paragraph = 3,
  Boolean = 4,
  Select = 5
}

export class SortableTableOptions {
  sortOrder?: SortDirection;
  hide?: boolean;
  dateFormat?: string;
  dropdownValues?: string[];
  sortable?: boolean;
}

export class Header {
  name: string;
  value: string;
  filterable: boolean;
  sortable: boolean;
  dataType: DataType;
  sortOrder: SortDirection;
  hide: boolean;
  dateFormat?: string;
  dropdownValues: string[];
  defaultValue: any;

  constructor(
    name: string,
    value: string,
    filterable: boolean,
    dataType?: DataType,
    options?: SortableTableOptions
  ) {
    this.name = name;
    this.value = value;
    this.filterable = filterable;
    this.sortable = false;

    if (!dataType) {
      this.dataType = DataType.String;
    } else {
      this.dataType = dataType;
    }

    if (options) {
      if (options.dropdownValues) {
        this.dropdownValues = options.dropdownValues;
      }
      if (options.dateFormat) {
        this.dateFormat = options.dateFormat;
      }
      if (options.hide) {
        this.hide = options.hide;
      }
      if (options.sortOrder) {
        this.sortOrder = options.sortOrder;
      }
      if (options.sortable) {
        this.sortable = options.sortable;
        this.sortOrder = SortDirection.None;
      }
    } else {
      this.sortOrder = SortDirection.None;
      this.hide = false;
      this.dateFormat = 'shortDate';
      this.dropdownValues = [];
    }
  }
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: `sortable-table`,
  styles: [
    `
      .row-disabled {
        pointer-events: none;
        opacity: 0.2;
        -webkit-transition: opacity 0.25s ease-in-out;
        -moz-transition: opacity 0.25s ease-in-out;
        -ms-transition: opacity 0.25s ease-in-out;
        -o-transition: opacity 0.25s ease-in-out;
      }
      .options {
        max-width: 15vw;
        width: 1vw;
      }
      .tr-form {
        display: table-row;
      }
    `
  ],
  templateUrl: 'sortableTable.html'
})
export class SortableTableComponent {
  @Output() delCall = new EventEmitter();
  @Output() editCall = new EventEmitter();
  @Output() moreCall = new EventEmitter();
  @Output() saveCall = new EventEmitter();
  @Output() addCall = new EventEmitter();

  @Input() showAdd: boolean;
  @Input() showDelete: boolean;
  @Input() showEdit: boolean;
  @Input() showMore: boolean;
  @Input() showCount: boolean;
  @Input() hideHeader: boolean;
  @Input() headers: Header[];
  @Input() filter: any;
  @Input() sorting: number;
  @Input() defaultSort: string;
  @Input() colorize: Function;

  sortedHeader: Header | null;
  editing: number;
  @Input('editing-data') duped: any;
  @Output('editing-data') dupedChanged = new EventEmitter();
  @Input('adding-data') newValue: any;
  @Output('adding-data') newValueChanged = new EventEmitter();

  omittedValues: any;

  @ViewChild('addForm')
  public addForm: NgForm;
  @ViewChild('editForm')
  public editForm: NgForm;

  constructor() {
    this.hideHeader = false;
    this.sortedHeader = null;
    this._data = [];
    this.sorting = -1;
    this.filter = {};
    this.headers = [];
    this.defaultSort = '';
    this.colorize = this.defColorize;
    this.showDelete = false;
    this.showEdit = false;
    this.showMore = false;
    this.editing = -1;
    this.newValue = {};
  }

  get showHeader(): boolean {
    return !this.hideHeader && this.headers.length > 0;
  }

  private _data: any[];

  get data() {
    return this._data;
  }

  @Input()
  set data(value: any[]) {
    this._data = value || [];
    this.sort();
  }

  toggleBoolean(obj: any, key: string) {
    if (!obj[key]) {
      obj[key] = false;
    }
    obj[key] = !obj[key];
  }

  cancel() {
    this.editing = -1;
    this.duped = null;
    this.newValue = {};
  }

  isOmitted(key: string, value: any): boolean {
    if (!this.omittedValues) {
      return false;
    }

    if (!this.omittedValues[key]) {
      return false;
    }

    return this.omittedValues[key].indexOf(value) !== -1;
  }

  toggleOmit(key: string, value: any) {
    if (!this.omittedValues) {
      this.omittedValues = {};
    }

    if (!this.omittedValues[key]) {
      this.omittedValues[key] = [];
    }

    if (this.omittedValues[key].indexOf(value) === -1) {
      this.omittedValues[key].push(value);
    } else {
      this.omittedValues[key].splice(this.omittedValues[key].indexOf(value), 1);
    }
  }

  add(data: any) {
    this.addCall.emit(data);
  }

  editRow(n: number): boolean {
    return this.editing === n;
  }

  disableRow(n: number): boolean {
    return this.editing !== n && this.editing > -1;
  }

  dupe(d: any): any {
    return JSON.parse(JSON.stringify(d));
  }

  save(d: any): any {
    this.saveCall.emit(d);
    this.editing = -1;
  }

  edit(d: any, index: number) {
    if (this.editCall.observers.length > 0) {
      this.editCall.emit(d);
    } else {
      this.duped = this.dupe(d);
      this.editing = index;
    }
  }

  del(d: any) {
    this.delCall.emit(d);
  }

  clear() {
    this.newValue = {};
    this.addForm?.form?.reset();
  }

  more(d: any) {
    this.moreCall.emit(d);
  }

  color(d: any): string {
    return this.colorize(d);
  }

  showOptions(): boolean {
    return this.showMore || this.showEdit || this.showDelete;
  }

  sort() {
    if (this.sorting !== -1) {
      this._data.sort(
        (a: any, b: any): number => {
          if (!this.sortedHeader) {
            return 0;
          }
          if (this.sortedHeader.sortOrder === SortDirection.Ascending) {
            if (a[this.sortedHeader.value] > b[this.sortedHeader.value]) {
              return 1;
            }
            if (a[this.sortedHeader.value] < b[this.sortedHeader.value]) {
              return -1;
            }
            return 0;
          }
          if (this.sortedHeader.sortOrder === SortDirection.Descending) {
            if (a[this.sortedHeader.value] < b[this.sortedHeader.value]) {
              return 1;
            }
            if (a[this.sortedHeader.value] > b[this.sortedHeader.value]) {
              return -1;
            }
            return 0;
          }
          return 0;
        }
      );
    } else
      if (this.defaultSort) {
        if (!this._data || this._data.length > 0) {
          this._data.sort((a: any, b: any) => {
            if (a[this.defaultSort] < b[this.defaultSort]) {
              return 1;
            }
            if (a[this.defaultSort] > b[this.defaultSort]) {
              return -1;
            }
            return 0;
          });
        }
      }
  }

  sortHeader(index: number) {
    // NO SORTING WHILE EDITTING
    if (this.editing !== -1) {
      return;
    }
    console.log(index);

    console.log(this.sorting);
    console.log(this.sortedHeader);

    if (this.sorting !== index && this.sorting >= 0) {
      this.headers[this.sorting].sortOrder = SortDirection.None;
    }

    this.sorting = index;
    switch (this.headers[this.sorting].sortOrder) {
      case SortDirection.None:
        this.headers[this.sorting].sortOrder = SortDirection.Ascending;
        break;
      case SortDirection.Ascending:
        this.headers[this.sorting].sortOrder = SortDirection.Descending;
        break;
      case SortDirection.Descending:
        this.headers[this.sorting].sortOrder = SortDirection.None;
        break;
    }

    this.sortedHeader = this.headers[this.sorting];
    if (this.headers[this.sorting].sortOrder === SortDirection.None) {
      this.headers[this.sorting].sortOrder = 0;
      this.sorting = -1;
    }

    this.sort();

    console.log(this.sorting);
    console.log(this.sortedHeader);
  }

  @HostListener('window:beforeunload', ['$event'])
  checkDirty($e: Event) {
    if (
      this.addForm &&
      this.addForm.form &&
      !this.addForm.form.pristine &&
      this.addForm.form.dirty
    ) {
      $e.preventDefault();
    }
  }

  private defColorize(): string {
    return '';
  }

  protected readonly SortDirection = SortDirection;
}
